/**
 * Email Campaign Management Page (Issue #376)
 *
 * Fair Manager page for managing email campaigns.
 * Lists campaigns, allows creating new campaigns, and viewing analytics.
 */

import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { fairManagerApi } from '../services/api';
import {
  EmailCampaignResponse,
  EmailCampaignListResponse,
} from '../types';
import { Mail, Plus, TrendingUp, Calendar, AlertCircle } from 'lucide-react';
import { EmailCampaignForm } from './EmailCampaignForm';
import { extractApiError } from '../utils/errorUtils'; // Issue #964: Standardized error handling

/**
 * Email Campaign Management page for Fair Managers
 */
export const EmailCampaignManagement: React.FC = () => {
  const navigate = useNavigate();
  const [campaigns, setCampaigns] = useState<EmailCampaignResponse[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [showCreateForm, setShowCreateForm] = useState(false);

  // Pagination
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const pageSize = 20;

  // Fair selection - FIXED (Issue #376 Code Review #6): Replace hardcoded test data with actual fair selector
  const [fairs, setFairs] = useState<{ id: number; name: string }[]>([]);
  const [selectedFairId, setSelectedFairId] = useState<number | null>(null);
  const [fairsLoading, setFairsLoading] = useState(true);
  const [fairsError, setFairsError] = useState<string | null>(null);

  // Derived values from selected fair
  const fairId = selectedFairId || 0;
  const fairName = fairs.find(f => f.id === selectedFairId)?.name || '';

  /**
   * Fetch managed fairs
   */
  useEffect(() => {
    const fetchFairs = async () => {
      setFairsLoading(true);
      setFairsError(null);

      try {
        const response = await fairManagerApi.getFairs();
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        setFairs(response.map((fair: any) => ({ id: fair.id, name: fair.name })));
        // Auto-select first fair if available
        if (response.length > 0 && !selectedFairId) {
          setSelectedFairId(response[0].id);
        }
      } catch (err: unknown) {
        const errorMessage = err && typeof err === 'object' && 'message' in err
          ? String(err.message)
          : 'Failed to load fairs';
        setFairsError(errorMessage);
      } finally {
        setFairsLoading(false);
      }
    };

    fetchFairs();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []); // Run once on mount

  /**
   * Fetch campaigns (only when fair is selected)
   */
  useEffect(() => {
    if (!selectedFairId) {
      return; // Don't fetch campaigns if no fair is selected
    }

    const fetchCampaigns = async () => {
      setIsLoading(true);
      setError(null);

      try {
        const response: EmailCampaignListResponse = await fairManagerApi.listEmailCampaigns(
          fairId,
          { page, page_size: pageSize }
        );
        setCampaigns(response.items);
        setTotalPages(response.total_pages);
      } catch (err: unknown) {
        const apiError = extractApiError(err);
        setError(apiError.message);
      } finally {
        setIsLoading(false);
      }
    };

    fetchCampaigns();
  }, [fairId, page, selectedFairId]);

  /**
   * Handle successful campaign creation
   */
  const handleCampaignCreated = (campaign: EmailCampaignResponse) => {
    setShowCreateForm(false);
    setCampaigns([campaign, ...campaigns]);
  };

  /**
   * View campaign analytics
   */
  const handleViewAnalytics = (campaignId: number) => {
    navigate(`/fairmanager/campaigns/${campaignId}/analytics`);
  };

  /**
   * Format date for display
   */
  const formatDate = (dateString: string): string => {
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  };

  return (
    <div className="max-w-7xl mx-auto p-6">
      {/* Header */}
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="text-3xl font-bold text-gray-800">Email Campaigns</h1>
          <p className="text-gray-600 mt-1">Manage promotional email campaigns for your fairs</p>
        </div>
        <button
          onClick={() => setShowCreateForm(true)}
          className="flex items-center gap-2 px-4 py-2 bg-primary text-white rounded-md hover:bg-primary-dark"
        >
          <Plus className="h-5 w-5" />
          <span>New Campaign</span>
        </button>
      </div>

      {/* Fair Selection */}
      {fairsLoading ? (
        <div className="mb-6 p-4 bg-gray-50 border border-gray-200 rounded-md flex items-center gap-2">
          <div className="animate-spin h-5 w-5 border-2 border-primary border-t-transparent rounded-full" />
          <span className="text-gray-600">Loading your fairs...</span>
        </div>
      ) : fairsError ? (
        <div className="mb-6 p-4 bg-red-50 border border-red-200 rounded-md flex items-center gap-2">
          <AlertCircle className="h-5 w-5 text-red-600" />
          <span className="text-red-800">{fairsError}</span>
        </div>
      ) : fairs.length > 0 ? (
        <div className="mb-6">
          <label htmlFor="fair-select" className="block text-sm font-medium text-gray-700 mb-2">
            Select Fair
          </label>
          <select
            id="fair-select"
            value={selectedFairId || ''}
            onChange={(e) => setSelectedFairId(Number(e.target.value))}
            className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary"
          >
            {fairs.map((fair) => (
              <option key={fair.id} value={fair.id}>
                {fair.name}
              </option>
            ))}
          </select>
        </div>
      ) : (
        <div className="mb-6 p-4 bg-yellow-50 border border-yellow-200 rounded-md flex items-center gap-2">
          <AlertCircle className="h-5 w-5 text-yellow-600" />
          <span className="text-yellow-800">You don't have any fairs to manage. Please create a fair first.</span>
        </div>
      )}

      {/* Create Campaign Form */}
      {showCreateForm && (
        <div className="mb-6">
          <EmailCampaignForm
            fairId={fairId}
            fairName={fairName}
            onSuccess={handleCampaignCreated}
            onCancel={() => setShowCreateForm(false)}
          />
        </div>
      )}

      {/* Error State */}
      {error && (
        <div className="mb-6 p-4 bg-red-50 border border-red-200 rounded-md flex items-center gap-2">
          <AlertCircle className="h-5 w-5 text-red-600" />
          <span className="text-red-800">{error}</span>
        </div>
      )}

      {/* Loading State */}
      {isLoading ? (
        <div className="flex items-center justify-center p-12">
          <div className="flex flex-col items-center gap-4">
            <div className="animate-spin h-12 w-12 border-4 border-primary border-t-transparent rounded-full" />
            <p className="text-gray-600">Loading campaigns...</p>
          </div>
        </div>
      ) : campaigns.length === 0 ? (
        /* Empty State */
        <div className="text-center p-12 bg-gray-50 rounded-lg border-2 border-dashed border-gray-300">
          <Mail className="h-16 w-16 text-gray-400 mx-auto mb-4" />
          <h3 className="text-lg font-semibold text-gray-700 mb-2">No campaigns yet</h3>
          <p className="text-gray-600 mb-4">
            Create your first email campaign to promote your fair to vendors
          </p>
          <button
            onClick={() => setShowCreateForm(true)}
            className="px-6 py-2 bg-primary text-white rounded-md hover:bg-primary-dark"
          >
            Create First Campaign
          </button>
        </div>
      ) : (
        /* Campaign List */
        <div className="space-y-4">
          {campaigns.map((campaign) => (
            <div
              key={campaign.id}
              className="bg-white p-6 rounded-lg shadow-md border border-gray-200 hover:border-teal-300 transition-colors"
            >
              <div className="flex items-start justify-between">
                <div className="flex-1">
                  <h3 className="text-lg font-semibold text-gray-800 mb-2">
                    {campaign.subject}
                  </h3>
                  <p className="text-gray-600 mb-4 line-clamp-2">{campaign.message}</p>

                  <div className="flex items-center gap-6 text-sm text-gray-500">
                    <div className="flex items-center gap-1">
                      <Mail className="h-4 w-4" />
                      <span>{campaign.total_recipients} recipients</span>
                    </div>
                    <div className="flex items-center gap-1">
                      <Calendar className="h-4 w-4" />
                      <span>
                        {campaign.sent_at
                          ? `Sent ${formatDate(campaign.sent_at)}`
                          : `Created ${formatDate(campaign.created_at)}`}
                      </span>
                    </div>
                    {campaign.discount_code && (
                      <div className="px-2 py-1 bg-teal-100 text-primary-dark rounded text-xs font-medium">
                        Code: {campaign.discount_code}
                      </div>
                    )}
                  </div>
                </div>

                <button
                  onClick={() => handleViewAnalytics(campaign.id)}
                  className="flex items-center gap-2 px-4 py-2 bg-purple-600 text-white rounded-md hover:bg-purple-700"
                >
                  <TrendingUp className="h-4 w-4" />
                  <span>Analytics</span>
                </button>
              </div>
            </div>
          ))}

          {/* Pagination */}
          {totalPages > 1 && (
            <div className="flex items-center justify-center gap-2 mt-6">
              <button
                onClick={() => setPage((p) => Math.max(1, p - 1))}
                disabled={page === 1}
                className="px-4 py-2 border border-gray-300 rounded-md text-gray-700 hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                Previous
              </button>
              <span className="text-gray-600">
                Page {page} of {totalPages}
              </span>
              <button
                onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
                disabled={page === totalPages}
                className="px-4 py-2 border border-gray-300 rounded-md text-gray-700 hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                Next
              </button>
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default EmailCampaignManagement;
